import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Clock
} from "lucide-react";

export default function PrioridadesTicketPage({ modulo = null, embedded = false }) {
  const [prioridades, setPrioridades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#F59E0B",
    nivel: 2,
    sla_hours: 24
  });
  const { getAuthHeaders } = useAuth();
  // Cabeceras con módulo (Hallazgos Auditoria) cuando aplica
  const authH = () => {
    const base = (getAuthHeaders)();
    return modulo ? { ...base, headers: { ...base.headers, "X-Modulo": modulo } } : base;
  };

  const colors = [
    { value: "#DC2626", label: "Rojo" },
    { value: "#F59E0B", label: "Naranja" },
    { value: "#3B82F6", label: "Azul" },
    { value: "#10B981", label: "Verde" },
    { value: "#6B7280", label: "Gris" },
    { value: "#8B5CF6", label: "Morado" },
    { value: "#EC4899", label: "Rosa" }
  ];

  useEffect(() => {
    fetchPrioridades();
  }, []);

  const fetchPrioridades = async () => {
    try {
      const res = await axios.get(`${API}/tickets/prioridades`, authH());
      setPrioridades(res.data);
    } catch (err) {
      toast.error("Error al cargar prioridades");
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setEditing(null);
    setFormData({ name: "", description: "", color: "#F59E0B", nivel: 2, sla_hours: 24 });
    setShowDialog(true);
  };

  const openEditDialog = (prio) => {
    setEditing(prio);
    setFormData({
      name: prio.name,
      description: prio.description || "",
      color: prio.color || "#F59E0B",
      nivel: prio.nivel || 2,
      sla_hours: prio.sla_hours ?? 24
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/tickets/prioridades/${editing.id}`, formData, authH());
        toast.success("Prioridad actualizada");
      } else {
        await axios.post(`${API}/tickets/prioridades`, formData, authH());
        toast.success("Prioridad creada");
      }
      setShowDialog(false);
      fetchPrioridades();
    } catch (err) {
      toast.error("Error al guardar prioridad");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Está seguro de eliminar esta prioridad?")) return;
    
    try {
      await axios.delete(`${API}/tickets/prioridades/${id}`, authH());
      toast.success("Prioridad eliminada");
      fetchPrioridades();
    } catch (err) {
      toast.error("Error al eliminar prioridad");
    }
  };

  return (
    <div className={embedded ? "flex" : "flex min-h-screen bg-slate-50 dark:bg-slate-900"}>
      {!embedded && <Sidebar />}
      
      <main className={`flex-1 p-4 md:p-8 ${embedded ? "pt-4 md:pt-4" : "pt-14 md:pt-8 ml-0 md:ml-64"} overflow-x-hidden`} data-testid="prioridades-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              Prioridades de Tickets
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Gestiona los niveles de prioridad para tickets
            </p>
          </div>
          
          <Button 
            onClick={openNewDialog}
            className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
            data-testid="new-prioridad-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Prioridad
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {prioridades.map((prio) => (
              <Card key={prio.id} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: prio.color }}
                      />
                      <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">
                        {prio.name}
                      </CardTitle>
                    </div>
                    <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-1 rounded">
                      Nivel {prio.nivel}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                    {prio.description || "Sin descripción"}
                  </p>
                  <div className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded" data-testid={`prioridad-sla-badge-${prio.id}`}>
                    <Clock className="w-3 h-3" />
                    SLA: {prio.sla_hours ?? 24}h
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(prio)}
                      className="flex-1"
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(prio.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#0C314C]" />
                {editing ? "Editar Prioridad" : "Nueva Prioridad"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Crítica, Alta, Media, Baja"
                  data-testid="prioridad-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Descripción de la prioridad"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Color</Label>
                  <Select
                    value={formData.color}
                    onValueChange={(value) => setFormData({...formData, color: value})}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: formData.color }}
                          />
                          {colors.find(c => c.value === formData.color)?.label || "Color"}
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {colors.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: color.value }}
                            />
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Nivel (1=más urgente)</Label>
                  <Select
                    value={formData.nivel.toString()}
                    onValueChange={(value) => setFormData({...formData, nivel: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Nivel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Máxima urgencia</SelectItem>
                      <SelectItem value="2">2 - Alta</SelectItem>
                      <SelectItem value="3">3 - Media</SelectItem>
                      <SelectItem value="4">4 - Baja</SelectItem>
                      <SelectItem value="5">5 - Muy baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>SLA — Tiempo de respuesta (horas) *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.sla_hours}
                  onChange={(e) => setFormData({...formData, sla_hours: parseInt(e.target.value) || 1})}
                  placeholder="Ej: 4, 8, 24, 72"
                  data-testid="prioridad-sla-input"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Tiempo máximo de atención para tickets con esta prioridad.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {editing ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
